import { motion } from "framer-motion";
import { useRubro, RUBROS, agendar } from "./rubro";

// ---------------------------------------------------------------------------
// HERO — "Más clientes, sin vueltas." + live inbox demo + rubro selector.
// Entrance animations on the left column; inbox panel slides in.
// ---------------------------------------------------------------------------

type InboxRow = {
  key: string;
  ch: string;
  name: string;
  text: string;
  status: string;
  bg: string;
  fg: string;
  rowBg: string;
  anim?: string;
};

function buildInbox(biz: string): InboxRow[] {
  return [
    {
      key: "1",
      ch: "WA",
      name: "María L.",
      text: "Hola, quería un presupuesto de...",
      status: "Nuevo",
      bg: "rgba(139,92,246,0.16)",
      fg: "#C4B5FD",
      rowBg: "transparent",
    },
    {
      key: "2",
      ch: "IG",
      name: "Jorge P.",
      text: "Me pasás precio por la instalación?",
      status: "Agendado",
      bg: "rgba(52,211,153,0.16)",
      fg: "#34D399",
      rowBg: "transparent",
    },
    {
      key: "3",
      ch: "FB",
      name: "Ana R.",
      text: "Te respondí el presupuesto, ¿lo...",
      status: "Seguimiento",
      bg: "rgba(242,182,90,0.16)",
      fg: "#F2B65A",
      rowBg: "transparent",
    },
    {
      key: "4",
      ch: "WA",
      name: "Carlos M.",
      text: "Gracias! Lo cierro el lunes 👍",
      status: "Ganado",
      bg: "rgba(139,92,246,0.16)",
      fg: "#A78BFA",
      rowBg: "transparent",
    },
  ];
}

export function HomeHero() {
  const { rb, rbIndex, setRubro } = useRubro();
  const inbox = buildInbox(rb.biz);

  const scrollToFlow = () => {
    const el = document.getElementById("flujo");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      data-screen-label="Hero"
      className="relative overflow-hidden border-b border-[#221F2A]"
    >
      {/* glow */}
      <div
        className="absolute pointer-events-none"
        style={{
          width: 620,
          height: 620,
          right: -120,
          top: -80,
          borderRadius: "50%",
          background:
            "radial-gradient(circle,rgba(139,92,246,0.14),transparent 65%)",
        }}
      />
      <div
        className="relative mx-auto px-6 grid gap-16 items-center"
        style={{
          maxWidth: 1240,
          paddingTop: "clamp(96px,9vw,128px)",
          paddingBottom: "clamp(56px,7vw,96px)",
          gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,440px),1fr))",
          minHeight: "min(78vh,760px)",
        }}
      >
        {/* Left */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        >
          <h1
            className="font-medium text-[#EEEDF2]"
            style={{
              fontSize: "clamp(40px,5.2vw,72px)",
              lineHeight: 1.02,
              letterSpacing: "-0.035em",
              textWrap: "balance" as never,
            }}
          >
            Más clientes,{" "}
            <span
              style={{
                background: "linear-gradient(90deg,#A78BFA,#E879F9)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
              }}
            >
              sin vueltas.
            </span>
          </h1>
          <p
            className="mt-[22px] max-w-[480px]"
            style={{
              fontSize: "clamp(17px,1.4vw,19px)",
              lineHeight: 1.55,
              color: "#BAB6C4",
              textWrap: "pretty" as never,
            }}
          >
            Web, reseñas, WhatsApp y seguimiento de presupuestos en un solo
            sistema. Lo armamos nosotros en 7 a 10 días.
          </p>

          <div className="flex flex-wrap items-center gap-5 mt-8">
            <button
              onClick={() => agendar("Hero - Agendar llamada")}
              className="bg-[#7C3AED] hover:bg-[#8B5CF6] text-white font-semibold rounded-[10px] transition-colors"
              style={{ padding: "14px 22px", fontSize: 16 }}
            >
              Agendar llamada de 20 min
            </button>
            <button
              onClick={scrollToFlow}
              className="text-[#BAB6C4] hover:text-[#EEEDF2] font-medium transition-colors"
              style={{ fontSize: 15 }}
            >
              Ver cómo funciona ↓
            </button>
          </div>

          {/* Rubro selector */}
          <div
            className="flex flex-wrap items-center gap-2.5 mt-10 pt-6 border-t border-[#221F2A]"
            style={{ fontSize: 14, color: "#9C98A6" }}
          >
            <span>Ver ejemplo para</span>
            <label className="relative inline-flex items-center">
              <select
                value={RUBROS[rbIndex].value}
                onChange={(e) => setRubro(e.target.value as never)}
                aria-label="Elegí tu rubro"
                className="appearance-none -webkit-appearance-none bg-[#17141F] text-[#EEEDF2] border border-[#332E3E] rounded-[8px] cursor-pointer font-medium"
                style={{ padding: "8px 34px 8px 12px" }}
              >
                {RUBROS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
              <span
                className="absolute pointer-events-none text-[#A78BFA]"
                style={{ right: 12, fontSize: 11 }}
              >
                ▼
              </span>
            </label>
            <span
              className="ml-auto font-mono"
              style={{ fontSize: "12.5px", color: "#6E6A7A" }}
            >
              $390.000/mes · sin contrato
            </span>
          </div>
        </motion.div>

        {/* Right — live inbox demo */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="bg-[#13111A] border border-[#2A2633] rounded-[18px] overflow-hidden"
          style={{
            boxShadow:
              "0 40px 100px -40px rgba(0,0,0,0.9),0 0 0 1px rgba(139,92,246,0.04)",
          }}
        >
          <div className="flex justify-between items-center px-[18px] py-[14px] border-b border-[#252130]">
            <div className="flex items-center gap-2.5">
              <span
                className="rounded-full bg-[#A78BFA]"
                style={{
                  width: 8,
                  height: 8,
                  animation: "pulse-subtle 1.6s infinite",
                }}
              />
              <span className="font-semibold text-[14px] text-[#EEEDF2]">
                {rb.biz}
              </span>
              <span className="text-[13px] text-[#9C98A6]">· Bandeja</span>
            </div>
            <span className="font-mono text-[11.5px] text-[#9C98A6]">
              DEMO EN VIVO
            </span>
          </div>

          <div className="flex flex-col">
            {inbox.map((m) => (
              <div
                key={m.key}
                className="grid items-center border-b border-[#1C1924] px-[18px] py-[14px]"
                style={{
                  gridTemplateColumns: "36px 1fr auto",
                  gap: 12,
                  background: m.rowBg,
                }}
              >
                <div
                  className="rounded-[10px] bg-[#1D1A26] text-[#BAB6C4] grid place-items-center font-mono font-semibold"
                  style={{ width: 36, height: 36, fontSize: 11 }}
                >
                  {m.ch}
                </div>
                <div className="min-w-0">
                  <div className="text-[14px] font-medium text-[#EEEDF2]">
                    {m.name}
                  </div>
                  <div className="text-[13px] text-[#9C98A6] mt-0.5 truncate">
                    {m.text}
                  </div>
                </div>
                <span
                  className="whitespace-nowrap rounded-full"
                  style={{
                    fontSize: "11.5px",
                    padding: "4px 9px",
                    background: m.bg,
                    color: m.fg,
                  }}
                >
                  {m.status}
                </span>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 border-t border-[#252130]">
            <div className="px-[18px] py-4 border-r border-[#252130]">
              <div className="font-mono text-[11.5px] text-[#9C98A6]">
                EN LA CALLE
              </div>
              <div className="text-[24px] font-semibold tracking-[-0.02em] mt-1 text-[#EEEDF2]">
                $ 8.420.000
              </div>
            </div>
            <div className="px-[18px] py-4">
              <div className="font-mono text-[11.5px] text-[#9C98A6]">
                GANADO ESTE MES
              </div>
              <div className="text-[24px] font-semibold tracking-[-0.02em] mt-1 text-[#A78BFA]">
                $ 3.150.000
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
